import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaBriefcase,
  FaTimes,
  FaSave,
  FaSpinner,
  FaCalendarAlt,
  FaClock,
  FaStethoscope,
} from "react-icons/fa";
import { buildApiUrl, API_ENDPOINTS } from "../utils/apiConfig";
import { getTimeSlotsForDate } from "../utils/appointmentSlots";
import "./AddPatientForm.css";

const AddPatientForm = ({ onClose, onPatientAdded }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    age: "",
    sex: "",
    address: "",
    occupation: "",
    appointmentDate: "",
    appointmentTime: "",
    serviceRequired: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "appointmentDate" ? { appointmentTime: "" } : {}),
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("Patient name is required");
      return false;
    }
    if (!formData.phone.trim()) {
      setError("Phone number is required");
      return false;
    }
    if (!formData.age || formData.age.trim() === "") {
      setError("Age is required");
      return false;
    }
    if (isNaN(formData.age) || formData.age < 0 || formData.age > 150) {
      setError("Please enter a valid age (0-150)");
      return false;
    }
    if (!formData.sex || formData.sex.trim() === "") {
      setError("Sex is required");
      return false;
    }
    if (!["Male", "Female", "Other"].includes(formData.sex)) {
      setError("Please select a valid sex option");
      return false;
    }
    return true;
  };

  // Services list
  const services = [
    "Angiography",
    "Angioplasty",
    "Stent Placement",
    "Valvuloplasty",
    "TAVR",
    "Mitral Valve Repair/Replacement",
    "Fractional Flow Reserve (FFR)",
    "Chronic Total Occlusion (CTO)",
    "Atherectomy",
    "Pericardiocentesis",
    "Cardiac Catheterization",
    "Endovenous Thermal Ablation",
    "ECG",
    "Heart Checkup",
    "Stress Test",
    "Echocardiography",
    "Preventive Cardiology",
    "Cardiac Consultation",
    "Percutaneous Device Closures",
    "Pacemaker Implantation",
    "Cardiac Resynchronising Therapy (CRT)",
    "AICD Implantation",
  ];

  const timeSlots = getTimeSlotsForDate(formData.appointmentDate);

  // Get minimum date (today) in IST
  const getMinDate = () => {
    const now = new Date();
    // Convert to IST (UTC+5:30)
    const istOffset = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in milliseconds
    const istTime = new Date(now.getTime() + istOffset);
    return istTime.toISOString().split("T")[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // First, add the patient
      const patientResponse = await fetch(
        buildApiUrl(API_ENDPOINTS.ADMIN.ADD_PATIENT),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            age: formData.age,
            sex: formData.sex,
            address: formData.address,
            occupation: formData.occupation,
            // Pass appointment details to prevent duplicate manual entry
            serviceRequired: formData.serviceRequired,
            preferredDate: formData.appointmentDate,
            preferredTime: formData.appointmentTime,
          }),
        }
      );

      const patientData = await patientResponse.json();

      if (patientData.success) {
        // If appointment details are provided, create an appointment
        if (
          formData.appointmentDate &&
          formData.appointmentTime &&
          formData.serviceRequired
        ) {
          try {
            const appointmentResponse = await fetch(
              buildApiUrl(API_ENDPOINTS.APPOINTMENTS.BOOK),
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  patientName: formData.name,
                  patientEmail: formData.email,
                  patientPhone: formData.phone,
                  patientAge: formData.age,
                  patientSex: formData.sex,
                  serviceRequired: formData.serviceRequired,
                  preferredDate: formData.appointmentDate,
                  preferredTime: formData.appointmentTime,
                  additionalInfo: `Patient created via admin panel. Address: ${
                    formData.address || "Not provided"
                  }, Occupation: ${formData.occupation || "Not provided"}`,
                  patientAddress: formData.address,
                  patientOccupation: formData.occupation,
                }),
              }
            );

            const appointmentData = await appointmentResponse.json();

            if (appointmentData.success) {
              setSuccess(
                "Patient added and appointment scheduled successfully!"
              );
            } else {
              setSuccess(
                "Patient added successfully, but appointment scheduling failed. You can schedule it manually later."
              );
            }
          } catch (appointmentError) {
            console.error("Error creating appointment:", appointmentError);
            setSuccess(
              "Patient added successfully, but appointment scheduling failed. You can schedule it manually later."
            );
          }
        } else {
          setSuccess("Patient added successfully!");
        }

        setTimeout(() => {
          onPatientAdded();
        }, 1500);
      } else {
        setError(patientData.message || "Failed to add patient");
      }
    } catch (error) {
      console.error("Error adding patient:", error);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <motion.div
      className="add-patient-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="add-patient-container"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="add-patient-header">
          <h2>
            <FaUser /> Add New Patient
          </h2>
          <button
            className="close-btn"
            onClick={handleClose}
            disabled={loading}
            title="Close"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="add-patient-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">
                <FaUser /> Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter patient's full name"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">
                <FaEnvelope /> Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter email address (optional)"
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">
                <FaPhone /> Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Enter phone number"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="age">
                <FaUser /> Age *
              </label>
              <input
                type="number"
                id="age"
                name="age"
                value={formData.age}
                onChange={handleInputChange}
                placeholder="Enter age"
                min="0"
                max="150"
                required
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="sex">
                <FaUser /> Sex *
              </label>
              <select
                id="sex"
                name="sex"
                value={formData.sex}
                onChange={handleInputChange}
                required
                disabled={loading}
              >
                <option value="">Select sex</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="address">
                <FaMapMarkerAlt /> Address
              </label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter patient's address"
                rows="3"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="occupation">
                <FaBriefcase /> Occupation
              </label>
              <input
                type="text"
                id="occupation"
                name="occupation"
                value={formData.occupation}
                onChange={handleInputChange}
                placeholder="Enter patient's occupation"
                disabled={loading}
              />
            </div>
          </div>

          {/* Appointment Section */}
          <div className="appointment-section">
            <h3
              style={{
                margin: "20px 0 15px 0",
                color: "#e03e33",
                fontSize: "1.1rem",
              }}
            >
              Schedule Appointment (Optional)
            </h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="serviceRequired">
                  <FaStethoscope /> Service Required
                </label>
                <select
                  id="serviceRequired"
                  name="serviceRequired"
                  value={formData.serviceRequired}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="">Select a service (optional)</option>
                  {services.map((service, index) => (
                    <option key={index} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="appointmentDate">
                  <FaCalendarAlt /> Appointment Date
                </label>
                <input
                  type="date"
                  id="appointmentDate"
                  name="appointmentDate"
                  value={formData.appointmentDate}
                  onChange={handleInputChange}
                  min={getMinDate()}
                  disabled={loading}
                />
                <small
                  style={{
                    color: "#666",
                    fontStyle: "italic",
                    marginTop: "5px",
                    display: "block",
                  }}
                >
                  Monday to Saturday, 6:00 PM - 9:00 PM.
                </small>
              </div>
              <div className="form-group">
                <label htmlFor="appointmentTime">
                  <FaClock /> Appointment Time
                </label>
                <select
                  id="appointmentTime"
                  name="appointmentTime"
                  value={formData.appointmentTime}
                  onChange={handleInputChange}
                  disabled={loading || !formData.appointmentDate}
                >
                  <option value="">Select time (optional)</option>
                  {timeSlots.map((time, index) => (
                    <option key={index} value={time.value}>
                      {time.display}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {error && (
            <div className="error-message">
              <span>❌ {error}</span>
            </div>
          )}

          {success && (
            <div className="success-message">
              <span>✅ {success}</span>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <FaSpinner className="spinning" /> Adding Patient...
                </>
              ) : (
                <>
                  <FaSave /> Add Patient
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default AddPatientForm;
